import React, { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, X } from 'lucide-react';

/**
 * Searchable site picker using Combobox pattern — fast and responsive.
 * Supports optional "All Sites" option for filter contexts.
 *
 * Keyboard filtering behaviour:
 *  - Typing in the search input filters sites by name, address, or postcode.
 *  - "No sites found." is shown when no option matches the current query.
 *  - The search resets when a value is selected OR when the popover is closed
 *    without a selection, so re-opening always shows the full list.
 *  - A clear (×) button inside the search field resets the query in-place
 *    without closing the dropdown.
 *
 * @param {Array} sites - Array of site objects with { id, name, address?, postcode? }
 * @param {string} value - Selected site id (or 'all' if showAllOption)
 * @param {function} onChange - Called with site id string (or 'all')
 * @param {boolean} disabled
 * @param {string} placeholder
 * @param {string} testId
 * @param {boolean} showAllOption - Show "All Sites" option at top
 * @param {string} allLabel - Label for the all option (default: "All Sites")
 */
export default function SitePicker({
  sites,
  value,
  onChange,
  disabled,
  placeholder = 'Search site...',
  testId,
  showAllOption = false,
  allLabel = 'All Sites',
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedSite = useMemo(() => {
    if (!value || value === 'all') return null;
    return sites.find(s => String(s.id) === String(value));
  }, [value, sites]);

  const displayText = value === 'all' ? allLabel : (selectedSite?.name || '');

  const filteredSites = useMemo(() => {
    if (!searchQuery) return sites.slice(0, 150);
    const q = searchQuery.toLowerCase();
    return sites.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.address || '').toLowerCase().includes(q) ||
      (s.postcode || '').toLowerCase().includes(q)
    );
  }, [sites, searchQuery]);

  // Reset the search query when the popover closes without a selection
  function handleOpenChange(nextOpen) {
    setOpen(nextOpen);
    if (!nextOpen) setSearchQuery('');
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`w-full justify-between font-normal h-9 text-sm ${!selectedSite && value !== 'all' ? 'text-muted-foreground' : ''}`}
          data-testid={testId}
        >
          <span className="truncate">{displayText || placeholder}</span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          {/* Search input with inline clear button */}
          <div className="relative">
            <CommandInput
              placeholder="Type to search sites..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              className={searchQuery ? 'pr-8' : ''}
              data-testid="site-picker-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground z-10"
                data-testid="site-picker-clear-search"
                aria-label="Clear site search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <CommandList>
            <CommandEmpty data-testid="site-picker-no-results">No sites found.</CommandEmpty>
            {showAllOption && (
              <>
                <CommandGroup>
                  <CommandItem
                    value="all"
                    onSelect={() => {
                      onChange('all');
                      setOpen(false);
                      setSearchQuery('');
                    }}
                  >
                    <Check className={`mr-2 h-3.5 w-3.5 ${value === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                    <span className="text-sm">{allLabel}</span>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup>
              {filteredSites.map(s => (
                <CommandItem
                  key={s.id}
                  value={String(s.id)}
                  onSelect={(val) => {
                    onChange(val === String(value) ? '' : val);
                    setOpen(false);
                    setSearchQuery('');
                  }}
                  data-testid={`site-option-${s.id}`}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${String(value) === String(s.id) ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="text-sm truncate">{s.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
